import React, { useState, useEffect, ReactNode } from 'react';
import { random } from 'lodash';
import { Text, Flex } from '@goodrx/matisse';
import { TypingIndicator } from 'app/components/TypingIndicator/TypingIndicator';
import { TriageBlockCodes, TriageDatabase } from './blocks';

export interface TriageBlockChildProps<DataType = any, InputValueType = any> {
  cursor: number;
  blockCode: string;
  isCurrent?: boolean;
  isPrevious?: boolean;
  dataValues: TriageDatabase;
  dataValue: DataType;
  inputValue: InputValueType;
  sessionId: string;
  setDataValue: <T extends keyof TriageDatabase>(key: T, value: TriageDatabase[T]) => void;
  setCurrentDataValue: (value: DataType) => void;
  setCurrentInputValue: (value: InputValueType) => void;
  addBlock: (blockCode: TriageBlockCodes, blockData?: TriageDatabase[TriageBlockCodes]) => void;
  replaceBlock: (blockCode: TriageBlockCodes, blockData?: TriageDatabase[TriageBlockCodes]) => void;
  removeBlock: () => void;
  onSubmit: (e?: React.SyntheticEvent) => void;
}

export interface TriageBlockConfig<DataType = any, InputValueType = any> {
  code: TriageBlockCodes;
  codePostfix?: boolean;
  Message: React.FC<TriageBlockChildProps<DataType, InputValueType>>;
  Response: React.FC<TriageBlockChildProps<DataType, InputValueType>>;
  SavedResponse: React.FC<TriageBlockChildProps<DataType, InputValueType>>;
  onSubmit?: (opts: Omit<TriageBlockChildProps<DataType, InputValueType>, 'onSubmit'>) => Promise<void> | void;
}

type TriageBlockProps = Omit<TriageBlockChildProps, 'onSubmit'> & TriageBlockConfig;

const BotMessage: React.FC<{ renderImmediately: boolean; onCompleteTyping: () => void; children: ReactNode }> = ({
  renderImmediately,
  onCompleteTyping,
  children,
}) => {
  const [showTypingIndicator, setShowTypingIndicator] = useState(!renderImmediately);

  useEffect(() => {
    const delay = renderImmediately ? 0 : random(500, 1500);
    if (renderImmediately) {
      onCompleteTyping();
    }

    const timeout = setTimeout(() => {
      setShowTypingIndicator(false);
      onCompleteTyping();
    }, delay);

    return () => clearTimeout(timeout);
  }, [renderImmediately]);

  if (showTypingIndicator) {
    return <TypingIndicator />;
  }

  return <>{children}</>;
};

export const TriageBlock: React.FC<TriageBlockProps> = ({
  cursor,
  blockCode,
  isCurrent,
  isPrevious,
  dataValues,
  dataValue,
  inputValue,
  sessionId,
  setCurrentDataValue,
  setCurrentInputValue,
  setDataValue,
  Message,
  Response,
  SavedResponse,
  onSubmit,
  addBlock,
  removeBlock,
  replaceBlock,
}) => {
  const [shouldDisplayUserInput, setShouldDisplayUserInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  function handleCompleteTyping() {
    setShouldDisplayUserInput(true);
  }

  const sharedProps = {
    cursor,
    blockCode,
    dataValues,
    dataValue,
    inputValue,
    setCurrentInputValue,
    setCurrentDataValue,
    setDataValue,
    isCurrent,
    isPrevious,
    addBlock,
    removeBlock,
    replaceBlock,
    sessionId,
  };

  async function handleSubmit(e?: React.SyntheticEvent) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      setErrorMessage('');

      if (onSubmit) {
        await onSubmit(sharedProps);
      }
    } catch (error) {
      setErrorMessage(error.message);
    }
  }

  const sharedComponentProps = {
    ...sharedProps,
    onSubmit: handleSubmit,
  };

  return (
    <Flex flexDirection="column">
      <BotMessage renderImmediately={!isCurrent} onCompleteTyping={handleCompleteTyping}>
        <Message {...sharedComponentProps} />
      </BotMessage>

      {shouldDisplayUserInput &&
        (isCurrent ? (
          <Flex justifyContent="flex-end" flexDirection="column" flex={1}>
            <Response {...sharedComponentProps} />

            {errorMessage && (
              <Text color="red" textAlign="right" mt={2}>
                {errorMessage}
              </Text>
            )}
          </Flex>
        ) : (
          <SavedResponse {...sharedComponentProps} />
        ))}
    </Flex>
  );
};
